import React, { useEffect, useRef } from "react";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { loadStoreCallVisitPostRequest, loadStoreCallVisitGetRequest } from "../redux/Actions";
import DefaultButton from "../../../shared/components/DefaultButton";
import FormikSelectField from "../../../shared/components/FormikSelectField";

const EVENT_TYPE_OPTIONS = ["Store Visit", "Store Call"];

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  event_date: Yup.string().required("Date of visit is required"),
  event_type: Yup.string().required("Event type is required"),
  description: Yup.string().required("Description is required"),
});

const StoreCallVisitForm = ({ handleClose, onSuccess, storeSap, storeName, ticketNumber }) => {
  const dispatch = useDispatch();
  const { loading, success, error, storevisits: rawVisits } = useSelector((state) => state.searchsap);
  const emp_no = JSON.parse(localStorage.getItem("emp_no"));
  const hasSubmitted = useRef(false);

  const storevisits = Array.isArray(rawVisits)
    ? rawVisits
    : Array.isArray(rawVisits?.data)
    ? rawVisits.data
    : [];

  const isTodayOrFuture = (eventDate) => {
    if (!eventDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const visitDate = new Date(eventDate);
    visitDate.setHours(0, 0, 0, 0);
    return visitDate >= today;
  };

  // ✅ Fetch visits when modal opens
  useEffect(() => {
    if (storeSap) {
      dispatch(loadStoreCallVisitGetRequest({ store_location_id: storeSap }));
    }
  }, [storeSap]);

  useEffect(() => {
    if (success && hasSubmitted.current) {
      hasSubmitted.current = false;
      if (onSuccess) onSuccess();
      if (handleClose) handleClose();
    }
  }, [success]);

  const initialValues = {
    name: "",
    event_date: new Date().toISOString().split("T")[0],
    event_type: "Store Visit",
    status: "",
    description: "",
    ticket_number: ticketNumber || "",
  };

  const handleSubmit = (values) => {
    hasSubmitted.current = true;
    const payload = {
      name: values.name,
      event_date: values.event_date,
      event_type: values.event_type,
      status: "Pending",
      description: values.description,
      created_by: emp_no,
      ticket_number: values.ticket_number || null,
      web: true,
      locations: [
        {
          filter_type: "store",
          store_location_id: storeSap,
          employee_id: null,
        },
      ],
    };
    dispatch(loadStoreCallVisitPostRequest(payload));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        // ✅ Computed inside Formik render so values is accessible
        const ticketFilter = ticketNumber || values.ticket_number;
        const futureVisits = storevisits.filter(
          (v) =>
            isTodayOrFuture(v.event_date) &&
            ticketFilter &&
            String(v.ticket_number).toLowerCase() === String(ticketFilter).toLowerCase()
        );

        return (
          <div className="p-2">

            {/* Store Name */}
            <div className="mb-2">
              <label className="form-label"><strong>Store Name</strong></label>
              <input
                type="text"
                className="form-control"
                value={storeName || storeSap || ""}
                readOnly
                style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
              />
            </div>

            {/* Ticket Number */}
            <div className="mb-2">
              <label className="form-label"><strong>Ticket Number</strong></label>
              <Field
                name="ticket_number"
                type="text"
                className="form-control"
                placeholder="Enter ticket number"
                disabled={!!ticketNumber}
                style={ticketNumber ? { backgroundColor: "#e9ecef", cursor: "not-allowed" } : {}}
              />
              <ErrorMessage name="ticket_number" component="div" className="text-danger small" />
            </div>

            {/* ✅ Reference table — shown when ticket number matches */}
            {futureVisits.length > 0 && (
              <div className="mb-3">
                <label className="form-label">
                  <strong>Upcoming Visits / Calls for Ticket #{ticketFilter} (Reference)</strong>
                </label>
                <div className="table-responsive" style={{ maxHeight: 125, overflowY: "auto" }}>
                  <table className="table table-sm table-bordered table-striped table-hover">
                    <thead className="table-secondary">
                      <tr>
                        <th>Ticket ID</th>
                        <th>Name</th>
                        <th style={{ whiteSpace: "nowrap" }}>Event Type</th>
                        <th style={{ whiteSpace: "nowrap" }}>Event Date</th>
                        <th>Status</th>
                        <th>Store</th>
                      </tr>
                    </thead>
                    <tbody>
                      {futureVisits.map((row) => {
                        const location = row.locations?.[0];
                        return (
                          <tr key={row.id}>
                            <td>
                              {row.ticket_id ? (
                                <a href={`/ticketing/${row.ticket_id}`} target="_blank" rel="noreferrer">
                                  {row.ticket_number ?? "-"}
                                </a>
                              ) : (
                                row.ticket_number ?? "-"
                              )}
                            </td>
                            <td>{row.name ?? "-"}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{row.event_type ?? "-"}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{row.event_date ?? "-"}</td>
                            <td>
                              <span
                                className={`badge ${
                                  row.status?.toLowerCase() === "completed"
                                    ? "bg-success"
                                    : row.status?.toLowerCase() === "pending"
                                    ? "bg-warning text-dark"
                                    : "bg-secondary"
                                }`}
                              >
                                {row.status ?? "-"}
                              </span>
                            </td>
                            <td>{location ? `${location.store_sap} - ${location.store_name}` : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Name */}
            <div className="mb-2">
              <label className="form-label"><strong>Name</strong></label>
              <Field
                name="name"
                type="text"
                className="form-control"
                placeholder="Enter visit name"
              />
              <ErrorMessage name="name" component="div" className="text-danger small" />
            </div>

            {/* Event Type */}
            <div className="mb-2">
              <label className="form-label"><strong>Event Type</strong></label>
              <FormikSelectField
                name="event_type"
                value={values.event_type}
                placeholder="Select event type"
                onChange={(name, val) => setFieldValue(name, val)}
              >
                {EVENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </FormikSelectField>
              <ErrorMessage name="event_type" component="div" className="text-danger small" />
            </div>

            {/* Date of Visit */}
            <div className="mb-2">
              <label className="form-label"><strong>Date of Visit</strong></label>
              <Field name="event_date" type="date" className="form-control" />
              <ErrorMessage name="event_date" component="div" className="text-danger small" />
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="form-label"><strong>Description</strong></label>
              <Field
                name="description"
                as="textarea"
                className="form-control"
                placeholder="Enter description"
                rows={4}
              />
              <ErrorMessage name="description" component="div" className="text-danger small" />
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-3">
              <DefaultButton
                buttonClass="btn btn-secondary"
                buttonTitle="Cancel"
                buttonType="button"
                actionBlock={handleClose}
              />
              <DefaultButton
                buttonClass="default-btn-color"
                buttonTitle="Submit"
                buttonType="button"
                actionBlock={handleSubmit}
              />
            </div>

          </div>
        );
      }}
    </Formik>
  );
};

export default StoreCallVisitForm;